import type { FC } from 'react'
import { CSSProperties, ReactNode, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Box, CircularProgress, Sx, useTheme } from '@dhl/theme'
import { useLocalStorage } from '@dhl/use-storage'

import { selectIsParcelShop } from '~/account/model/selectors'
import { links } from '~/links'
import { ReduxState } from '~/redux/createRootReducer'
import { Header } from './Header'
import { SideNavigation } from './SideNavigation'
import { getConfig } from '~/config'

type MakeStylesProps = {
  isExpanded: boolean
  isMobile: boolean
}

const useStyles = ({ isExpanded, isMobile }: MakeStylesProps): Record<string, Sx> => ({
  root: {
    display: 'flex',
  },
  drawerContainer: {
    // eslint-disable-next-line no-nested-ternary
    width: isMobile ? 0 : isExpanded ? 220 : 100,
    position: 'relative',
  },
  drawerPaper: {
    width: isExpanded ? 200 : 80,
    overflowX: 'hidden',
  },
  toolbarSpacer: (theme) => ({
    ...(theme.mixins.toolbar as CSSProperties),
  }),
  container: {
    padding: (theme) => theme.spacing(2),
    flex: 1,
    paddingBottom: '56px',
  },
  progress: {
    zIndex: 3,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginLeft: '-20px',
    marginTop: '-20px',
  },
})

type Props = {
  children?: ReactNode
  isLoading?: boolean
  hideNavigation?: boolean
  testId?: string
  isParcelShop: boolean
  isLoggedIn: boolean | null
}

const pwaUrl = '/pwa/shipment/package'
const drawerWidth = 200

const PageComponent: FC<Props> = ({
  children,
  isParcelShop,
  isLoggedIn,
  isLoading = false,
  hideNavigation = false,
  testId = '',
}) => {
  const {
    device: { isDesktop, isMobile },
  } = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>('isNavigationExpanded', isDesktop)
  const classes = useStyles({ isExpanded, isMobile })
  const navigate = useNavigate()
  const { navigation } = getConfig()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const toggle = () => setIsExpanded(!isExpanded)

  const handleProfileClick = () => {
    const isPortalProfileEnabled = !isParcelShop && navigation === 'portal'
    const link = isPortalProfileEnabled ? links.portalProfileLink() : links.profileOverviewLink()

    if (isPortalProfileEnabled) {
      window.location.href = `${link}?initialReferrer=${encodeURIComponent(pwaUrl)}`
    } else {
      navigate(links.profileOverviewLink(), {
        state: { referrerUrl: pwaUrl },
      })
    }
  }

  return (
    <Box data-test={!isLoading && testId} sx={classes.root}>
      <Header
        drawerWidth={drawerWidth}
        hideNavigation={hideNavigation}
        isLoggedIn={isLoggedIn || false}
        isParcelShop={isParcelShop}
        onClick={() => setMobileOpen(!mobileOpen)}
        onProfileClick={handleProfileClick}
      />
      <Box sx={classes.drawerContainer}>
        {isMobile && (
          <SideNavigation drawerWidth={drawerWidth} isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
        )}

        {isDesktop && (
          <>
            <Box sx={classes.toolbarSpacer} />
            <SideNavigation drawerWidth={drawerWidth} isExpanded={isExpanded} toggleExpanded={toggle} />
          </>
        )}
      </Box>
      {isLoading && <CircularProgress sx={classes.progress} />}
      {!isLoading && (
        <Box sx={classes.container}>
          <Box sx={classes.toolbarSpacer} />
          {children}
        </Box>
      )}
    </Box>
  )
}

const mapStateToProps = (state: ReduxState) => ({
  ...selectIsParcelShop(state),
  isLoggedIn: state.account.isLoggedIn,
  showPrintless: state.config.showPrintless,
})

export const Page = connect(mapStateToProps)(PageComponent)
